import { useEffect, useState, type FormEvent } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { SlideContent } from 'lorena-templates'
import { pb } from '../../lib/pocketbase'
import { slugify } from '../../lib/slug'
import { useAuth } from '../../state/AuthContext'
import { SlidePreviewFrame } from './components/SlidePreviewFrame'
import { StatusSelect } from './components/StatusSelect'
import type { MateriaStatus } from './lib/status'
import { slideToCtx } from './lib/slideCtx'
import type { SlideDraft } from './types'

interface Materia {
  id: string
  title: string
  slug: string
  status: MateriaStatus
  updated: string
}

interface SlideRecord {
  id: string
  materia: string
  template_id: string
  position: number
  content: SlideContent
  image?: string
  image_original?: string
  image_transform?: SlideDraft['imageTransform']
  updated?: string
}

function toDraft(record: SlideRecord): SlideDraft {
  return {
    id: record.id,
    materiaId: record.materia,
    templateId: record.template_id,
    position: record.position,
    content: record.content ?? {},
    imageFileId: record.image || undefined,
    originalImageFileId: record.image_original || undefined,
    imageTransform: record.image_transform ?? null,
    updated: record.updated,
  }
}

export function MateriaListPage() {
  const { user } = useAuth()
  const [materias, setMaterias] = useState<Materia[]>([])
  const [covers, setCovers] = useState<Record<string, SlideDraft | null>>({})
  const [title, setTitle] = useState('')
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [renaming, setRenaming] = useState<Materia | null>(null)
  const [renameTitle, setRenameTitle] = useState('')
  const [deleting, setDeleting] = useState<Materia | null>(null)
  const [savingAction, setSavingAction] = useState(false)
  const [actionError, setActionError] = useState('')

  useEffect(() => {
    pb.collection('materias')
      .getFullList<Materia>({ sort: '-updated' })
      .then(async (list) => {
        setMaterias(list)
        const entries = await Promise.all(
          list.map(async (m) => {
            try {
              const first = await pb.collection('slides').getFirstListItem<SlideRecord>(`materia="${m.id}"`, { sort: 'position' })
              return [m.id, toDraft(first)] as const
            } catch {
              return [m.id, null] as const
            }
          }),
        )
        setCovers(Object.fromEntries(entries))
      })
      .finally(() => setLoading(false))
  }, [])

  const onChangeStatus = async (materiaId: string, status: MateriaStatus) => {
    setMaterias((prev) => prev.map((m) => (m.id === materiaId ? { ...m, status } : m)))
    await pb.collection('materias').update(materiaId, { status })
  }

  const onCreate = async (event: FormEvent) => {
    event.preventDefault()
    if (!title.trim() || !user) return
    setCreating(true)
    try {
      const materia = await pb.collection('materias').create<Materia>({
        title: title.trim(),
        slug: `${slugify(title)}-${Date.now().toString(36)}`,
        status: 'rascunho',
        template_set: 'masculino',
        owner: user.id,
      })
      setMaterias((prev) => [materia, ...prev])
      setTitle('')
    } finally {
      setCreating(false)
    }
  }

  const openRename = (materia: Materia) => {
    setActionError('')
    setRenameTitle(materia.title)
    setRenaming(materia)
  }

  const onRename = async (event: FormEvent) => {
    event.preventDefault()
    const nextTitle = renameTitle.trim()
    if (!renaming || !nextTitle || nextTitle === renaming.title) {
      setRenaming(null)
      return
    }
    setSavingAction(true)
    setActionError('')
    try {
      const updated = await pb.collection('materias').update<Materia>(renaming.id, { title: nextTitle })
      setMaterias((prev) => prev.map((item) => (item.id === updated.id ? { ...item, title: updated.title, updated: updated.updated } : item)))
      setRenaming(null)
    } catch {
      setActionError('Não foi possível renomear o carrossel.')
    } finally {
      setSavingAction(false)
    }
  }

  const onDelete = async () => {
    if (!deleting) return
    setSavingAction(true)
    setActionError('')
    try {
      await pb.collection('materias').delete(deleting.id)
      setMaterias((prev) => prev.filter((item) => item.id !== deleting.id))
      setCovers((prev) => {
        const next = { ...prev }
        delete next[deleting.id]
        return next
      })
      setDeleting(null)
    } catch {
      setActionError('Não foi possível apagar o carrossel.')
    } finally {
      setSavingAction(false)
    }
  }

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20 }}>Criador de Mídias</h1>

      <form
        onSubmit={onCreate}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 28,
          maxWidth: 480,
        }}
      >
        <input
          placeholder="Nome da matéria (ex.: Entrevista Fulano de Tal)"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
        <button type="submit" disabled={creating || !title.trim()}>
          <Plus size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
          Nova matéria
        </button>
      </form>

      {loading && <p style={{ color: 'var(--muted)' }}>Carregando…</p>}

      {!loading && materias.length === 0 && (
        <p style={{ color: 'var(--muted)' }}>Nenhuma matéria ainda — crie a primeira acima.</p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16 }}>
        {materias.map((materia) => {
          const cover = covers[materia.id]
          return (
            <div
              key={materia.id}
              style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--paper)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              {/* select fica FORA do <Link> de propósito — um <select> dentro de <a>
                  é inválido (controles interativos não podem se aninhar) e causava
                  o clique abrir a matéria em vez de trocar o status. */}
              <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 5 }}>
                <StatusSelect value={materia.status} onChange={(status) => onChangeStatus(materia.id, status)} />
              </div>
              <Link
                to={`/criador-de-midias/${materia.id}`}
                aria-label={`Abrir carrossel ${materia.title}`}
                style={{ display: 'flex', textDecoration: 'none', color: 'var(--ink)' }}
              >
                <div
                  style={{
                    aspectRatio: '1080/1350',
                    background: 'var(--surface)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {cover ? (
                    <SlidePreviewFrame templateId={cover.templateId} content={cover.content} ctx={slideToCtx(cover)} displayWidth={160} />
                  ) : (
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>Sem telas</span>
                  )}
                </div>
              </Link>
              <div className="materia-card-footer">
                <Link to={`/criador-de-midias/${materia.id}`} className="materia-card-title">
                    {materia.title}
                </Link>
                <div className="materia-card-actions">
                  <button type="button" className="materia-card-action" onClick={() => openRename(materia)} title="Renomear carrossel" aria-label={`Renomear ${materia.title}`}>
                    <Pencil size={15} aria-hidden="true" />
                  </button>
                  <button type="button" className="materia-card-action danger" onClick={() => { setActionError(''); setDeleting(materia) }} title="Apagar carrossel" aria-label={`Apagar ${materia.title}`}>
                    <Trash2 size={15} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {renaming && (
        <div className="modal-backdrop" onClick={() => !savingAction && setRenaming(null)}>
          <form className="modal-card materia-action-modal" role="dialog" aria-modal="true" aria-labelledby="rename-title" onSubmit={onRename} onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2 id="rename-title">Renomear carrossel</h2>
            </div>
            <label htmlFor="carousel-name">Nome do carrossel</label>
            <input id="carousel-name" autoFocus value={renameTitle} onChange={(event) => setRenameTitle(event.target.value)} maxLength={200} />
            {actionError && <p className="materia-action-error">{actionError}</p>}
            <div className="materia-action-buttons">
              <button type="button" className="secondary" disabled={savingAction} onClick={() => setRenaming(null)}>Cancelar</button>
              <button type="submit" disabled={savingAction || !renameTitle.trim()}>{savingAction ? 'Salvando…' : 'Salvar nome'}</button>
            </div>
          </form>
        </div>
      )}

      {deleting && (
        <div className="modal-backdrop" onClick={() => !savingAction && setDeleting(null)}>
          <div className="modal-card materia-action-modal" role="dialog" aria-modal="true" aria-labelledby="delete-title" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2 id="delete-title">Apagar carrossel?</h2>
            </div>
            <p className="materia-delete-copy">“{deleting.title}” e todas as suas telas serão apagados permanentemente.</p>
            {actionError && <p className="materia-action-error">{actionError}</p>}
            <div className="materia-action-buttons">
              <button type="button" className="secondary" disabled={savingAction} onClick={() => setDeleting(null)}>Cancelar</button>
              <button type="button" className="danger-button" disabled={savingAction} onClick={onDelete}>{savingAction ? 'Apagando…' : 'Apagar carrossel'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
